import { BUILD_ID } from './buildId';

/** The shell index.html renders the canvas into; a failed bootstrap has nothing else to draw on. */
export const GAME_CONTAINER_ID = 'game-container';

export const BOOTSTRAP_FAILURE_TITLE = 'Tower Power failed to start';
export const BOOTSTRAP_FAILURE_ADVICE =
  'Reload the page. If it keeps happening, use Report a bug in Settings so we can see what went wrong.';

export interface BootstrapFailureOptions {
  container: HTMLElement | null;
  buildId?: string;
  onReload?: () => void;
}

/**
 * What a failed bootstrap leaves instead of a blank shell. The game never drew a frame, so this says
 * in plain DOM what happened, names the build a report will carry, and offers the one action that
 * usually helps. Styled inline (like the game's other DOM panels) with the system font stack: the
 * webfont may be part of what failed to load.
 */
export function renderBootstrapFailure(options: BootstrapFailureOptions): HTMLElement | null {
  const { container } = options;
  if (!container) return null;

  const buildId = options.buildId ?? BUILD_ID;
  const reload = options.onReload ?? ((): void => window.location.reload());

  const panel = document.createElement('div');
  panel.id = 'bootstrap-failure';
  panel.setAttribute('role', 'alert');
  panel.style.cssText = `
    position: absolute;
    inset: 0;
    z-index: 5;
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    gap: 14px;
    padding: 24px;
    box-sizing: border-box;
    text-align: center;
    background: rgba(8, 12, 7, 0.94);
    font-family: ui-sans-serif, system-ui, -apple-system, BlinkMacSystemFont, "Segoe UI", sans-serif;
  `;

  const title = document.createElement('h1');
  title.textContent = BOOTSTRAP_FAILURE_TITLE;
  title.style.cssText = 'margin: 0; color: #f4e2b6; font-size: 24px; line-height: 1.25;';

  const advice = document.createElement('p');
  advice.textContent = BOOTSTRAP_FAILURE_ADVICE;
  advice.style.cssText = 'margin: 0; max-width: 46ch; color: rgba(244, 226, 182, 0.82); font-size: 15px; line-height: 1.5;';

  const build = document.createElement('p');
  build.textContent = `Build ${buildId}`;
  build.style.cssText = 'margin: 0; color: rgba(244, 226, 182, 0.6); font-size: 12px;';

  const reloadButton = document.createElement('button');
  reloadButton.type = 'button';
  reloadButton.textContent = 'Reload';
  reloadButton.style.cssText = `
    padding: 12px 28px;
    min-height: 44px;
    border: 0;
    border-radius: 4px;
    background: #c6a85f;
    color: #1a1206;
    font: bold 15px ui-sans-serif, system-ui, sans-serif;
    cursor: pointer;
  `;
  reloadButton.addEventListener('click', () => reload());

  panel.appendChild(title);
  panel.appendChild(advice);
  panel.appendChild(build);
  panel.appendChild(reloadButton);
  container.appendChild(panel);
  // Land keyboard focus on the only action so the panel is usable without a pointer.
  reloadButton.focus();
  return panel;
}
